"use client";

import { MainLayout } from "@/components/layout/main-layout";
import { MemoizedMarkdown } from "@/components/docs/memoized-markdown";
import { useChat } from "@ai-sdk/react";
import { RouteGuard } from "@/components/auth/route-guard";
import {
  useEffect,
  useRef,
  useMemo,
  useState,
  ReactNode,
  memo,
  useCallback,
  createContext,
  useContext,
} from "react";
import { useRouter } from "next/navigation";
import ChatSidebar, {
  useSidebar,
  SidebarContext,
} from "@/components/layout/chat-sidebar";
import { useNav } from "@/components/providers/nav-context";
import { Skeleton } from "@/components/ui/skeleton";
import { Canvas, useCanvas, CanvasProvider } from "@/components/ui/canvas/canvas";
import { extractFileBlocks, FileBlock } from "@/components/docs/memoized-markdown";
import { Button } from "@/components/ui/button";
import { MessageCircle, PanelRightClose, SquarePen } from "lucide-react";

// Loading skeleton component for Chat page
function ChatLoadingSkeleton() {
  return (
    <div className="h-full flex flex-col relative">
      {/* Sidebar skeleton */}
      <div className="fixed left-0 top-[57px] bottom-0 w-[280px] border-r bg-muted/50 p-4">
        <Skeleton className="h-10 w-full mb-4" />
        <div className="space-y-2">
          {[...Array(5)].map((_, idx) => (
            <Skeleton key={idx} className="h-14 w-full" />
          ))}
        </div>
      </div>

      {/* Main chat area skeleton */}
      <div className="flex flex-col h-full overflow-hidden ml-[280px]">
        <div className="flex-1 overflow-y-auto py-6 px-6 space-y-6">
          <div className="flex items-center justify-center h-full mt-[6rem]">
            <div className="text-center max-w-3xl mx-auto space-y-4">
              <Skeleton className="h-12 w-64 mx-auto" />
              <Skeleton className="h-4 w-96 mx-auto" />
            </div>
          </div>
        </div>

        {/* Input area skeleton */}
        {/* <div className=" fixed bottom-0 left-[40px] right-0 bg-background/95 backdrop-blur-sm z-10">
          <div className="mx-auto max-w-3xl">
            <div className="flex gap-2">
              <Skeleton className="h-[50px] w-full rounded-lg" />
            </div>
            <Skeleton className="h-4 w-72 mx-auto mt-2" />
          </div>
        </div> */}
      </div>
    </div>
  );
}

// Create a context for sharing chat state
interface ChatContextType {
  messages: any[];
  input: string; 
  isLoading: boolean;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  setInput: (input: string) => void;
  error: Error | null;
  fileBlocks?: FileBlock[];
  activeFileBlock?: FileBlock | null;
  setActiveFileBlock?: (file: FileBlock) => void;
  expandCanvas?: () => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

// Custom hook to use the chat context
const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};

// Define MessageInput component
const MessageInput = () => {
  const { input, handleInputChange, isLoading, error, messages, fileBlocks = [] } =
    useChatContext();
  const { isOpen } = useSidebar();
  const { isExpanded } = useCanvas();
  const router = useRouter();
  const [localInput, setLocalInput] = useState(input);
  
  // Check if chat has started
  const chatHasStarted = messages.length > 0;
  // Check if we have file blocks to display in canvas
  const hasFileBlocks = fileBlocks.length > 0;

  useEffect(() => {
    setLocalInput(input);
  }, [input]);

  // Modified submit handler - creates a new dynamic route on first message
  const handleMessageSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // If this is the first message, generate a UUID and navigate
      if (localInput.trim()) {
        const newChatId = typeof crypto !== "undefined" && crypto.randomUUID 
          ? crypto.randomUUID() 
          : Math.random().toString(36).slice(2, 10);
        // We need to store the current input to restore it after navigation
        sessionStorage.setItem('pendingChatMessage', localInput.trim());
        // Navigate to the dynamic route
        router.push(`/chat/${newChatId}`);
        return;
      }
    },
    [localInput, router]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (localInput.trim()) {
          const syntheticEvent = {
            preventDefault: () => {},
          } as React.FormEvent<HTMLFormElement>;
          handleMessageSubmit(syntheticEvent);
        }
      }
    },
    [localInput, handleMessageSubmit]
  );

  const handleLocalInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalInput(e.target.value);
      handleInputChange(e);
    },
    [handleInputChange]
  );

  if (chatHasStarted) {
    return (
      <div
        className={`
          fixed bottom-0
          backdrop-blur-sm z-10
          transition-all duration-300 ease-in-out
          ${isOpen ? 'left-[280px]' : 'left-0'}
          ${(isExpanded && hasFileBlocks) ? 'right-[50%] md:right-[50%]' : 'right-0'}
          w-auto flex justify-center pb-2
          px-2 sm:px-0
        `}
        style={{
          width: isOpen
            ? (isExpanded && hasFileBlocks ? 'calc(100% - 280px - 50%)' : 'calc(100% - 280px)')
            : (isExpanded && hasFileBlocks ? 'calc(100% - 50%)' : '100%')
        }}
      >
        <form
          onSubmit={handleMessageSubmit}
          className="relative w-full max-w-3xl"
          autoComplete="off"
        >
          <div
            className="border border-border rounded-3xl bg-background px-4 md:px-6 py-4 md:py-5 relative flex items-center min-h-[60px] md:min-h-[80px] transition-colors duration-300 w-full mx-auto "
            style={{ borderColor: 'hsl(var(--primary))' }}
          >
            <textarea
              value={localInput}
              onChange={handleLocalInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask Codiv"
              className="w-full bg-transparent outline-none border-none resize-none text-base md:text-lg text-foreground placeholder:text-muted-foreground min-h-[32px] max-h-[80px] pr-16"
              disabled={isLoading}
              rows={1}
              style={{ overflow: "auto" }}
            />
            <button
              type="submit"
              className="absolute right-4 bottom-1/2 translate-y-1/2 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center transition-colors hover:bg-primary/90"
              disabled={!localInput.trim() || isLoading}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </form>
      </div>
    );
  } else {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <form
          onSubmit={handleMessageSubmit}
          className="relative w-full max-w-3xl"
          autoComplete="off"
        >
          <div
            className="border border-border rounded-3xl bg-background px-4 md:px-6 py-4 md:py-5 relative flex items-center min-h-[60px] md:min-h-[80px] transition-colors duration-300 w-full mx-auto "
            style={{ borderColor: 'hsl(var(--primary))' }}
          >
            <textarea
              value={localInput}
              onChange={handleLocalInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask Codiv"
              className="w-full bg-transparent outline-none border-none resize-none text-base md:text-lg text-foreground placeholder:text-muted-foreground min-h-[32px] max-h-[80px] pr-16"
              disabled={isLoading}
              rows={1}
              style={{ overflow: "auto" }}
            />
            <button
              type="submit"
              className="absolute right-4 bottom-1/2 translate-y-1/2 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center transition-colors hover:bg-primary/90"
              disabled={!localInput.trim() || isLoading}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </form>
      </div>
    );
  }
};

// Chat message component 
const ChatMessage = memo(
  ({ message, isUser }: { message: any; isUser: boolean }) => {
    return (
      <div className="flex justify-center w-full px-1 sm:px-0 mt-[3rem]">
        <div className="w-full max-w-3xl flex justify-end">
          <div className={`flex ${isUser ? "justify-end w-[85%]" : "justify-center w-full"} `}>
            <div className={`
              p-3 sm:p-4
              rounded-lg 
              prose 
              prose-sm sm:prose-base md:prose-lg 
              dark:prose-invert 
              ${isUser ? "bg-primary/70 text-primary-foreground dark:bg-primary/20" : ""}
              max-w-full
              overflow-hidden
              break-words
            `}>
              <div className="overflow-x-auto w-full">
                <MemoizedMarkdown id={message.id} content={message.content} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isUser === nextProps.isUser &&
      prevProps.message.id === nextProps.message.id &&
      prevProps.message.content === nextProps.message.content
    );
  }
);

ChatMessage.displayName = "ChatMessage";

// Full page chat component
const FullPageChat = ({
  messagesContainerRef,
}: {
  messagesContainerRef: React.RefObject<HTMLDivElement>;
}) => {
  const { messages, isLoading, error } = useChatContext();

  const isThinking = useMemo(() => {
    if (!isLoading) return false;
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    return !lastMessage || lastMessage.role !== "assistant";
  }, [isLoading, messages]);

  const thinkingContent = useMemo(() => {
    return `
*Reading documents...*
    `;
  }, []);

  const errorContent = useMemo(() => {
    if (!error) return "";
    return `
## Error
${error.message || "Something went wrong. Please try again."}
${error.cause ? `**Cause:** ${String(error.cause)}` : ""}
${error.stack ? `
<details>
<summary>Error Stack</summary>
\`\`\`
${error.stack}
\`\`\`
</details>` : ""}
*Check the console and server logs for more details.*
    `;
  }, [error]);

  return (
    <div
      ref={messagesContainerRef}
      className={`flex-1 overflow-y-auto py-6 px-6 space-y-6 ${
        messages.length > 0 ? "pb-40": "" // Keep padding-bottom for input area
      }`}
    >
      {messages.length === 0 && !isThinking ? (
        <div className="flex items-center justify-center h-full mt-[6rem]">
          <div className="text-center max-w-3xl mx-auto">
            <MessageCircle className="mx-auto mb-4 w-16 h-16 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-xl font-semibold text-center text-muted-foreground">What can I help with?</p>
          </div>
        </div>
      ) : (
        messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            isUser={message.role === "user"}
          />
        ))
      )}
      {isThinking && (
        <div className="flex justify-start">
          <div className="p-4 rounded-lg animate-pulse">
            <MemoizedMarkdown id="thinking" content={thinkingContent} />
          </div>
        </div>
      )}
      {error && (
        <div className="flex justify-start">
          <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
            <MemoizedMarkdown id="error" content={errorContent} />
          </div>
        </div>
      )}
    </div>
  );
};

// Chat provider component for main chat page (not a dynamic route)
const ChatProvider = ({ children }: { children: ReactNode }) => {
  // Initialize with empty state since this is a new chat
  const chatState = useChat({
    id: "new-chat",
    api: "/api/chat",
    experimental_throttle: 250,
    initialMessages: [],
    onError: (err) => {
      console.error("Chat API error:", err);
      console.error("Error details:", JSON.stringify(err));
    },
  });
  
  // Track detected file blocks from AI responses
  const [fileBlocks, setFileBlocks] = useState<FileBlock[]>([]);
  // Track currently selected file block for Canvas
  const [activeFileBlock, setActiveFileBlock] = useState<FileBlock | null>(null);
  // Access the Canvas context
  const { setIsExpanded } = useCanvas();

  // Extract file blocks from AI responses
  useEffect(() => {
    if (chatState.messages && chatState.messages.length > 0) {
      // Only look at assistant messages
      const assistantMessages = chatState.messages.filter(msg => msg.role === 'assistant');
      if (assistantMessages.length > 0) {
        // Get all file blocks from all assistant messages
        let allFileBlocks: FileBlock[] = [];
        
        assistantMessages.forEach(message => {
          if (message && message.content) {
            // Extract file blocks from the message content
            const extractedBlocks = extractFileBlocks(message.content);
            if (extractedBlocks.length > 0) {
              allFileBlocks = [...allFileBlocks, ...extractedBlocks];
            }
          }
        });
        
        // Update file blocks if we found any
        if (allFileBlocks.length > 0) {
          console.log('Found file blocks:', allFileBlocks);
          setFileBlocks(allFileBlocks);
          
          // Set the first file block as active by default if none is selected
          if (!activeFileBlock && allFileBlocks.length > 0) {
            setActiveFileBlock(allFileBlocks[0]);
            // Automatically open the canvas when we detect file blocks
            setIsExpanded(true);
          }
        }
      }
    }
  }, [chatState.messages, activeFileBlock, setIsExpanded]);

  // Extend chat context with file-related state and functions
  const extendedChatState = {
    ...chatState,
    error: chatState.error ?? null, // Ensure error is null if undefined
    fileBlocks,
    activeFileBlock,
    setActiveFileBlock,
    expandCanvas: () => {
      setIsExpanded(true);
    }
  };

  return (
    <ChatContext.Provider value={extendedChatState}>
      {children}
      {/* Render Canvas if we have file blocks */}
      {fileBlocks.length > 0 && (
        <Canvas 
          files={fileBlocks.map((block, index) => ({
            id: `${block.name}-${index}`, // Create truly unique IDs by appending index
            name: block.name,
            content: block.content,
            language: block.language
          }))}
          activeFileId={activeFileBlock?.name ? `${activeFileBlock.name}-${fileBlocks.findIndex(f => f.name === activeFileBlock.name && f.content === activeFileBlock.content)}` : undefined}
          readOnly={true}
        />
      )}
    </ChatContext.Provider>
  );
};

// StaticRouteSidebarProvider with open default state
const StaticRouteSidebarProvider = ({ children }: { children: ReactNode }) => {
  // Initialize with open state for the main chat page
  const [isOpen, setIsOpen] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const refreshChatHistory = () => setRefreshKey((k) => k + 1);

  return (
    <SidebarContext.Provider value={{ isOpen, toggleSidebar, refreshChatHistory, refreshKey }}>
      {children}
    </SidebarContext.Provider>
  );
};

// Chat content component
const ChatContent = ({
  messagesContainerRef,
}: {
  messagesContainerRef: React.RefObject<HTMLDivElement>;
}) => {
  const { isOpen, toggleSidebar } = useSidebar();
  const { isExpanded } = useCanvas();
  const router = useRouter();
  // Get access to fileBlocks from the context
  const { fileBlocks = [] } = useChatContext();
  const hasFileBlocks = fileBlocks.length > 0;
  // Check for mobile/small screen
  const [isMobile, setIsMobile] = useState(false);
  
  // Effect to check screen size and update state
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is standard md breakpoint
    };
    // Check on initial render
    checkMobile();
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Function to handle starting a new chat
  const startNewChat = () => {
    // Generate a unique chat id (use crypto.randomUUID if available, else fallback)
    const id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString();
    router.push(`/chat/${id}`);
  };

  return (
    <>
      {/* Sidebar toggle and new chat buttons - responsive positioning */}
      {!isOpen && (
        <div className="fixed top-20 z-50">
          {/* On mobile: sidebar toggle on left, new chat on right */}
          <div className="md:hidden w-screen flex justify-between px-4">
            <Button
              variant="outline"
              size="icon"
              className="transition-all duration-300 border"
              style={{ borderColor: 'hsl(var(--primary))' }}
              onClick={toggleSidebar}
              aria-label="Expand sidebar"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="transition-all duration-300 border"
              style={{ borderColor: 'hsl(var(--primary))' }}
              onClick={startNewChat}
              aria-label="New chat"
            >
              <SquarePen className="h-4 w-4" />
            </Button>
          </div>
          
          {/* On tablet and desktop: both buttons on left side */}
          <div className="hidden md:flex space-x-2 ml-4">
            <Button
              variant="outline"
              size="icon"
              className="transition-all duration-300 border"
              style={{ borderColor: 'hsl(var(--primary))' }}
              onClick={toggleSidebar}
              aria-label="Expand sidebar"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="transition-all duration-300 border"
              style={{ borderColor: 'hsl(var(--primary))' }}
              onClick={startNewChat}
              aria-label="New chat"
            >
              <SquarePen className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className={`
        h-full flex flex-col relative 
        transition-all duration-300 ease-in-out 
        ${isOpen ? "ml-[280px]" : "ml-[0px]"}
        ${(isExpanded && hasFileBlocks) ? "mr-[50%] md:mr-[50%]" : "mr-0"}
        ${(isOpen && isMobile) ? "opacity-0 pointer-events-none" : "opacity-100"}
      `}>
        <FullPageChat messagesContainerRef={messagesContainerRef} />
        <MessageInput />
      </div>
    </>
  );
};

export default function Chat() {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { setHideMainNavItems } = useNav();

  // Set hideMainNavItems to true when this component mounts
  useEffect(() => {
    setHideMainNavItems(true);
    
    // Clean up by setting hideMainNavItems back to false when component unmounts
    return () => {
      setHideMainNavItems(false);
    };
  }, [setHideMainNavItems]);
  
  return (
    <MainLayout hideFooter={true}>
      <RouteGuard loadingComponent={<ChatLoadingSkeleton />}>
        <StaticRouteSidebarProvider> {/* Custom sidebar provider with open default state */}
          <CanvasProvider defaultExpanded={false}> {/* Add CanvasProvider */}
            <ChatProvider> {/* Chat data provider */}
              <ChatSidebar /> {/* Render Sidebar here - no activeChatId since this isn't a specific chat */}
              <ChatContent messagesContainerRef={messagesContainerRef} /> {/* Render Content */}
            </ChatProvider>
          </CanvasProvider>
        </StaticRouteSidebarProvider>
      </RouteGuard>
    </MainLayout>
  );
}